import React from 'react';
import { Tag, Tooltip, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faCommentSms,
  faCircleCheck,
  faEnvelopeOpenText,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';
import { CaseTable, RowActionButton, OutreachHeartLoader } from '../styled';
import OutreachTableSkeleton from './OutreachTableSkeleton';

const getRiskColor = (riskLevel) => {
  const colors = {
    critical: '#ff4d4f',
    high: '#fa8c16',
    medium: '#faad14',
    low: '#52c41a',
    minimal: '#1890ff'
  };
  return colors[riskLevel] || '#d9d9d9';
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : null);

/**
 * Shared table listing members with outreach status and per-row email / text
 * actions. `variant="activity"` swaps the risk/findings columns for an
 * activity-focused layout (what was sent, when, and who's engaging).
 */
const OutreachMembersTable = ({
  members,
  loading,
  selectedRowKeys,
  onSelectionChange,
  onEmail,
  onText,
  onRowClick,
  scrollY,
  variant = 'default',
  pagination,
  emptyText = 'No high-risk members to display'
}) => {
  const memberColumn = {
    title: 'Member',
    key: 'member',
    width: 230,
    render: (_, record) => (
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.textDark }}>
          {record.name || 'Unknown'}
        </div>
        <div style={{ color: COLORS.textMedium, fontSize: 12, marginTop: 2 }}>
          {record.phone || 'No phone'}
        </div>
        <div style={{ color: COLORS.textMedium, fontSize: 12 }}>
          {record.email || 'No email'}
        </div>
      </div>
    )
  };

  const actionsColumn = {
    title: 'Actions',
    key: 'actions',
    width: 110,
    align: 'center',
    fixed: 'right',
    render: (_, record) => (
      <Space>
        <Tooltip title={record.email ? 'Send email' : 'No email on file'}>
          <RowActionButton
            disabled={!record.email}
            onClick={() => onEmail([record])}
            aria-label="Send email"
          >
            <FontAwesomeIcon icon={faEnvelope} />
          </RowActionButton>
        </Tooltip>
        <Tooltip title={record.phone ? 'Send text' : 'No phone on file'}>
          <RowActionButton
            disabled={!record.phone}
            onClick={() => onText([record])}
            aria-label="Send text"
          >
            <FontAwesomeIcon icon={faCommentSms} />
          </RowActionButton>
        </Tooltip>
      </Space>
    )
  };

  const baseColumns = [
    memberColumn,
    {
      title: 'Risk',
      key: 'risk',
      width: 110,
      sorter: (a, b) => (a.riskScore || 0) - (b.riskScore || 0),
      render: (_, record) => (
        <div>
          <Tag color={getRiskColor(record.riskLevel)} style={{ marginBottom: 4 }}>
            {record.riskLevel ? record.riskLevel.toUpperCase() : 'N/A'}
          </Tag>
          <div style={{ fontSize: 11, color: COLORS.textMedium }}>
            Score: {record.riskScore ?? '—'}
          </div>
        </div>
      )
    },
    {
      title: 'Critical Findings',
      key: 'findings',
      width: 200,
      render: (_, record) => {
        const findings = record.criticalFindings || [];
        if (!findings.length) {
          return <span style={{ color: COLORS.textMuted, fontSize: 12 }}>None flagged</span>;
        }
        const [first, ...rest] = findings;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <Tooltip title={first}>
              <Tag
                color="volcano"
                style={{
                  fontSize: 11,
                  maxWidth: 150,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  margin: 0
                }}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} /> {first}
              </Tag>
            </Tooltip>
            {rest.length > 0 && (
              <Tooltip title={rest.join(', ')}>
                <Tag style={{ fontSize: 11, margin: 0 }}>+{rest.length}</Tag>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: 'Outreach Status',
      key: 'status',
      width: 220,
      render: (_, record) => {
        const o = record.outreach || {};
        if (!o.contacted) {
          return <Tag color="default">Not contacted</Tag>;
        }
        return (
          <Space size={[4, 6]} wrap>
            {o.emailSent && (
              <Tooltip title={`Emailed ${formatDate(o.emailSentDate) || ''}`}>
                <Tag color="blue" icon={<FontAwesomeIcon icon={o.emailOpened ? faEnvelopeOpenText : faEnvelope} />}>
                  {o.emailOpened ? 'Email opened' : 'Emailed'}
                </Tag>
              </Tooltip>
            )}
            {o.textSent && (
              <Tooltip title={`Texted ${formatDate(o.textSentDate) || ''}`}>
                <Tag color="cyan" icon={<FontAwesomeIcon icon={faCommentSms} />}>Texted</Tag>
              </Tooltip>
            )}
            {(o.replied ?? false) && (
              <Tag color="green" icon={<FontAwesomeIcon icon={faCircleCheck} />}>Replied</Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Last Contact',
      key: 'lastContact',
      width: 120,
      render: (_, record) => formatDate(record.outreach?.lastContact) || (
        <span style={{ color: COLORS.textMuted }}>Never</span>
      )
    },
    actionsColumn
  ];

  const activityColumns = [
    memberColumn,
    {
      title: 'Event',
      key: 'event',
      width: 160,
      render: (_, record) => (
        <span style={{ fontSize: 13, color: COLORS.textMedium }}>{record.eventName || '—'}</span>
      )
    },
    {
      title: 'Sent',
      key: 'sent',
      width: 190,
      render: (_, record) => {
        const o = record.outreach || {};
        if (!o.emailSent && !o.textSent) {
          return <span style={{ color: COLORS.textMuted, fontSize: 12 }}>—</span>;
        }
        const line = (icon, color, label, date) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, lineHeight: '22px' }}>
            <FontAwesomeIcon icon={icon} style={{ color, width: 14 }} />
            <span style={{ color: COLORS.textDark, fontWeight: 500 }}>{label}</span>
            <span style={{ color: COLORS.textMuted, fontSize: 12 }}>{formatDate(date) || ''}</span>
          </div>
        );
        return (
          <div>
            {o.emailSent && line(faEnvelope, COLORS.$primaryBlue, 'Email', o.emailSentDate)}
            {o.textSent && line(faCommentSms, '#2E8B8B', 'Text', o.textSentDate)}
          </div>
        );
      }
    },
    {
      title: 'Engagement',
      key: 'engagement',
      width: 170,
      render: (_, record) => {
        const o = record.outreach || {};
        const hasReplied = o.replied ?? o.responded;
        if (!o.emailOpened && !hasReplied) {
          return <span style={{ color: COLORS.textMuted, fontSize: 12 }}>Awaiting response</span>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {o.emailOpened && (
              <Tag
                color="blue"
                style={{ margin: 0, borderRadius: 999, paddingInline: 10 }}
                icon={<FontAwesomeIcon icon={faEnvelopeOpenText} />}
              >
                Opened
              </Tag>
            )}
            {hasReplied && (
              <Tag
                color="green"
                style={{ margin: 0, borderRadius: 999, paddingInline: 10 }}
                icon={<FontAwesomeIcon icon={faCircleCheck} />}
              >
                Replied
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Last Activity',
      key: 'lastActivity',
      width: 120,
      sorter: (a, b) =>
        new Date(a.outreach?.lastContact || 0) - new Date(b.outreach?.lastContact || 0),
      render: (_, record) => (
        <span style={{ color: COLORS.textMedium, fontSize: 13 }}>
          {formatDate(record.outreach?.lastContact) || '—'}
        </span>
      )
    },
    actionsColumn
  ];

  const columns = variant === 'activity' ? activityColumns : baseColumns;

  if (loading) {
    return (
      <OutreachTableSkeleton
        columns={columns}
        rows={8}
        scroll={{ x: 1000, y: scrollY }}
        showSelection={!!onSelectionChange}
      />
    );
  }

  return (
    <CaseTable
      columns={columns}
      dataSource={members}
      loading={{ spinning: loading, indicator: <OutreachHeartLoader /> }}
      rowKey="patientId"
      rowSelection={
        onSelectionChange
          ? { selectedRowKeys, onChange: onSelectionChange, columnWidth: 48 }
          : undefined
      }
      onRow={onRowClick ? (record) => ({
        style: { cursor: 'pointer' },
        onClick: (e) => {
          // Ignore clicks on the checkbox or action buttons.
          if (
            e.target.closest('button') ||
            e.target.closest('.ant-checkbox-wrapper') ||
            e.target.closest('.ant-checkbox')
          ) return;
          onRowClick(record);
        }
      }) : undefined}
      locale={{ emptyText }}
      pagination={pagination !== undefined ? pagination : {
        defaultPageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} members`
      }}
      className="event-management-pagination"
      scroll={{ x: 1000, y: scrollY }}
    />
  );
};

export default OutreachMembersTable;
